import { useState } from "react";
import { Lock, Mail, ShieldCheck, Loader2, AlertTriangle, KeyRound } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Login failed.");
      return;
    }
    if (res.mfaRequired) {
      setChallenge(res.challenge);
      setPassword("");
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await verifyMfa(challenge, code, remember);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Verification failed.");
      setCode("");
    }
  };

  return (
    <div
      className="min-h-screen w-screen flex items-center justify-center bg-[#09090B] text-zinc-100 grid-bg p-6"
      data-testid="login-root"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <img src="/virtue-logo.png" alt="" className="h-5 w-auto block" data-testid="login-logo" />
          <span className="font-mono text-[11px] tracking-[0.28em] text-zinc-200 uppercase">
            Virtue Health
          </span>
        </div>

        <div className="border border-white/10 bg-[#0a0a0c]">
          <div className="px-6 pt-6 pb-5 border-b border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-emerald-300">
                Restricted Access
              </span>
            </div>
            <h1 className="font-display font-black text-3xl leading-[1] tracking-tight text-zinc-50">
              {challenge ? "VERIFY" : "SIGN IN"}
            </h1>
          </div>

          {challenge ? (
            <form onSubmit={onVerify} className="p-6 space-y-4" data-testid="mfa-challenge-form">
              <p className="text-[13px] leading-relaxed text-zinc-400">
                {useRecovery
                  ? "Enter one of your saved recovery codes."
                  : "Enter the 6-digit code from your authenticator app."}
              </p>
              <div>
                <label
                  htmlFor="mfa-code"
                  className="block font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5"
                >
                  {useRecovery ? "Recovery Code" : "Verification Code"}
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                  <input
                    id="mfa-code"
                    value={code}
                    autoFocus
                    inputMode={useRecovery ? "text" : "numeric"}
                    autoComplete="one-time-code"
                    onChange={(e) =>
                      setCode(
                        useRecovery
                          ? e.target.value.trim().slice(0, 20)
                          : e.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    placeholder={useRecovery ? "xxxx-xxxx-xxxx" : "000000"}
                    required
                    data-testid="mfa-code"
                    className="w-full bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none pl-9 pr-3 py-2.5 font-mono text-[15px] tracking-[0.3em] text-zinc-100 placeholder:text-zinc-700 transition-colors"
                  />
                </div>
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 border border-rose-400/30 bg-rose-400/5 px-3 py-2"
                  data-testid="login-error"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
                  <span className="font-mono text-[11px] text-rose-200 leading-relaxed">
                    {error}
                  </span>
                </div>
              )}

              <label
                htmlFor="mfa-remember"
                className="flex items-start gap-2.5 cursor-pointer select-none group"
              >
                <input
                  id="mfa-remember"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="mfa-remember-device"
                  className="mt-0.5 w-4 h-4 shrink-0 accent-emerald-400 bg-[#09090B] border border-white/20 cursor-pointer"
                />
                <span className="font-mono text-[10px] leading-relaxed tracking-[0.1em] uppercase text-zinc-500 group-hover:text-zinc-300 transition-colors">
                  Remember this device for 30 days
                  <span className="block normal-case tracking-normal text-[10px] text-zinc-600 mt-0.5">
                    Only on this browser. Your password is still required.
                  </span>
                </span>
              </label>

              <button
                type="submit"
                disabled={submitting || !code}
                data-testid="mfa-verify-submit"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-emerald-400/60 bg-emerald-400/10 hover:bg-emerald-400/20 disabled:opacity-50 disabled:cursor-not-allowed font-mono text-[11px] tracking-[0.22em] uppercase text-emerald-200 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying…
                  </>
                ) : (
                  <>Verify →</>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setCode("");
                  setError("");
                }}
                data-testid="mfa-toggle-recovery"
                className="w-full px-4 py-2 border border-white/10 hover:border-white/25 font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {useRecovery ? "Use authenticator code" : "Use a recovery code"}
              </button>
            </form>
          ) : (
          <form onSubmit={onSubmit} className="p-6 space-y-4">
            <div>
              <label
                htmlFor="login-email"
                className="block font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5"
              >
                Work Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  autoFocus
                  autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@virtuealliance.com"
                  required
                  data-testid="login-email"
                  className="w-full bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none pl-9 pr-3 py-2.5 font-mono text-[13px] text-zinc-100 placeholder:text-zinc-700 transition-colors"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="login-pw"
                className="block font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input
                  id="login-pw"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  data-testid="login-password"
                  className="w-full bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none pl-9 pr-3 py-2.5 font-mono text-[13px] text-zinc-100 placeholder:text-zinc-700 transition-colors"
                />
              </div>
            </div>

            {error && (
              <div
                className="flex items-start gap-2 border border-rose-400/30 bg-rose-400/5 px-3 py-2"
                data-testid="login-error"
              >
                <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
                <span className="font-mono text-[11px] text-rose-200 leading-relaxed">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !email || !password}
              data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-emerald-400/60 bg-emerald-400/10 hover:bg-emerald-400/20 disabled:opacity-50 disabled:cursor-not-allowed font-mono text-[11px] tracking-[0.22em] uppercase text-emerald-200 transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Verifying…
                </>
              ) : (
                <>Authenticate →</>
              )}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
